import {
  Box,
  Paper,
  IconButton,
  Stack,
  Table,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  SxProps,
  Switch,
  FormControlLabel,
} from "@mui/material";
import React, {
  useEffect,
  useState,
  useMemo,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import {
  setAllReviewed,
  setForecastStatus,
  setForecastNote,
  setSelectedTemplateType,
  setSelectedMetric,
  setSelectedMonth,
  setSelectedSite,
  setMonthlyData,
  setLastMonthActiveUsers,
} from "../../../../../features/forecastersSlice";
import HistoryIcon from "@mui/icons-material/History";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { NumericFormat } from "react-number-format";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { forecastersEndPointLogic } from "../../../../../logic/AssetManagement/Forecasters/forecastersEndPointLogic";
import store from "../../../../../store";
import MetricsByMonthDialogue from "../ForeCasterInfo/MetricsByMonth/MetricsByMonth";
import { calculateNonEditableMonths } from "../../../../../utils/ForcasterUtils";
import { Theme } from "@emotion/react";
import ForecastSkeleton from "./ForecastSkeleton";
import { NonEditableFTD } from "./NonEditableMonths/NonEditableFTD";
import "../ForeCasterInfo/FTDinfo.css";
import { TableWithOffsetScrollbar } from "../ForeCasterInfo/TableWithOffsetScrollbar";
import { getPreviousMonthActiveUsers } from "../../../../../utils/helpers";
import InfoCard from "./InfoCard";
import { FTD_CONFIG } from "./helpers/Forecastcopyhelper";
import { useForecastCopy } from "../../../../../customHooks/useForecastCopy";
import { FIELD_NAMES, getFieldInfo } from "./helpers/FTDConfig";
import QuarterSummaryCell from "./QuarterSummaryCell";

interface Props {
  LastYearData: any;
  RequiredDataLastMonths: any;
  loading: any;
  setLoading: any;
}

const NewForecastForFTD = ({
  LastYearData,
  RequiredDataLastMonths,
  loading,
  setLoading,
}: Props) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [editableNRPU, setEditableNRPU] = useState<string | null>(null);
  const [editableGrowthTarget, setEditableGrowthTarget] = useState<
    string | null
  >(null);
  const [editableAvgNetREv, setEditableAvgNetREv] = useState<string | null>(
    null,
  );
  const [editableCPAPV, setEditableCPAPV] = useState<string | null>(null);
  const [editableCVR, setEditableCVR] = useState<string | null>(null);
  const [editableProfit, setEditableProfit] = useState<string | null>(null);
  const [editableChurnRate, setEditableChurnRate] = useState<string | null>(
    null,
  );
  const [editableAcquiredToFtd, setEditableAcquiredToFtd] = useState<
    string | null
  >(null);
  const [editableRsP, setEditableRsP] = useState<string | null>(null);
  const [editableRsBrNg, setEditableRsBrNg] = useState<string | null>(null);
  const [forecastRows, setForecastRows] = useState([]) as any;
  const [showOnlyForecasts, setShowOnlyForecasts] = useState(false);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const [currentDate] = useState(new Date());
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTemplateType = useSelector(
    (state: any) => state?.forecasters?.selectedTemplateType,
  );
  const selectedYear = searchParams.get("selectedYear") as any;
  const forecastLastTwelveMonthsData = useSelector(
    (state: any) => state?.forecasters?.forecastLastTwelveMonthsData,
  );
  const selectedSite = searchParams.get("selectedSite") as any;
  const { monthlyData } = useSelector((state: any) => state?.forecasters);
  const forecastNote = useSelector(
    (state: any) => state?.forecasters?.forcastNote,
  );
  const forecastStatus = useSelector(
    (state: any) => state.forecasters.forecastStatus,
  );
  const forecasters = useSelector(
    (state: any) => state.forecasters?.forecastersList,
  );
  const { lastMonthActiveUsers } = useSelector(
    (state: any) => state.forecasters,
  );
  const [nonEditableQuarterTotals, setNonEditableQuarterTotals] = useState<
    any[]
  >([]);

  const handleNonEditableQuarterTotals = useCallback((totals: any[]) => {
    setNonEditableQuarterTotals(totals);
  }, []);

  const [selectedFieldInfo, setSelectedFieldInfo] = useState<{
    name: string;
    type: "User input" | "Parameter" | "Formula";
    valueText: string;
    impactText: string;
  } | null>(null);

  // ── cell-click highlight state ──────────────────────────────────────
  const [selectedCell, setSelectedCell] = useState<{
    fieldName: string;
    month: string;
  } | null>(null);

  // Derive the set of formula field-names for the currently selected cell.
  const highlightedFieldNames = useMemo(() => {
    if (!selectedCell) return new Set<string>();
    const info = getFieldInfo(selectedCell.fieldName);
    if (!info?.formulaFields) return new Set<string>();

    const aliases: Record<string, string> = {
      "net rev": "estimated net revenue",
      rs: "estimated revenue share",
      "est. cpa income": "estimated cpa income",
      "cpa income": "estimated cpa income",
    };

    const formulaFieldsSet = new Set<string>();
    info.formulaFields.split(",").forEach((s: string) => {
      const trimmed = s.trim().toLowerCase();
      formulaFieldsSet.add(trimmed);
      if (aliases[trimmed]) {
        formulaFieldsSet.add(aliases[trimmed]);
      }
    });

    return formulaFieldsSet;
  }, [selectedCell]);

  // Returns true when fieldLabel is one of the formula source fields of the
  // currently-selected cell AND the row month matches.
  const isHighlighted = (fieldLabel: string, rowMonth: string) => {
    if (!selectedCell || selectedCell.month !== rowMonth) return false;
    return highlightedFieldNames.has(fieldLabel.toLowerCase());
  };

  const handleFieldClick = (fieldName: string) => {
    const fieldData = getFieldInfo(fieldName);
    if (!fieldData) {
      setSelectedFieldInfo(null);
      return;
    }

    // Build the value text
    const valueText =
      fieldData.type === "Formula"
        ? `The formula for ${fieldName} is: ${fieldData.formula}` || ""
        : fieldData.glossaryText || "";

    // Build the impact text
    const impactText =
      fieldData.type === "Formula"
        ? fieldData.affectedFields
          ? `Changes in '${fieldName}', you will affect: ${fieldData.affectedFields}`
          : `Changes in '${fieldName}' will not affect other metrics directly.`
        : fieldData.affectedFields
          ? `By editing the '${fieldName}', you will affect: ${fieldData.affectedFields}`
          : `Changes in '${fieldName}' will not affect other metrics directly.`;

    setSelectedFieldInfo({
      name: fieldName,
      type: fieldData.type,
      valueText,
      impactText,
    });
  };

  // Combined click handler: sets selectedCell AND calls the existing
  // handleFieldClick so the InfoCard still updates.
  const handleCellClick = (fieldName: string, month: string) => {
    setSelectedCell({ fieldName, month });
    handleFieldClick(fieldName);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthNames = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    [],
  );

  const editableStates = {
    editableNRPU,
    editableGrowthTarget,
    editableAvgNetREv,
    editableCPAPV,
    editableCVR,
    editableProfit,
    editableChurnRate,
    editableAcquiredToFtd,
    editableRsP,
    editableRsBrNg,
  };

  const LastPublishedVersion = forecasters
    ?.filter(
      (item: any) => item?.template_type === "1" && item?.status === "publish",
    )
    .reduce(
      (max: any, item: any) => {
        return item.version > max.version ? item : max;
      },
      { version: -Infinity },
    );

  const isForecastFetchable = (targetMonth: string): boolean => {
    if (!targetMonth) return false;
    const [month, year] = targetMonth.split("/").map(Number);
    const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
    const deadline = new Date(year, quarterStartMonth - 1, 15);
    const today = new Date();
    return today <= deadline;
  };

  const RequiredPublishedMonthData = LastPublishedVersion?.forecaster_content
    ?.filter((target: any) => {
      const [year, month] = target.month.split("-");
      const formattedMonth = `${parseInt(month)}/${year}`;
      return (
        !isForecastFetchable(formattedMonth) &&
        parseInt(year) === parseInt(selectedYear)
      );
    })
    ?.map((row: any) => {
      const [, month] = row?.month.split("-").map(Number);
      const monthName = monthNames[month - 1]; // Get month name for display
      const transformedRow = { ...row, displayMonth: monthName }; // Add displayMonth for UI
      return transformedRow;
    });

  // Helper function to get month name
  const getMonthName = (monthIndex: any) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthIndex];
  };

  const LastData = useMemo(
    () => forecastLastTwelveMonthsData?.query_output || [],
    [forecastLastTwelveMonthsData?.query_output],
  );

  useEffect(() => {
    if (!LastData || LastData.length === 0) return;
    const value = getPreviousMonthActiveUsers(LastData);
    dispatch(setLastMonthActiveUsers(value));
  }, [LastData, dispatch]);

  const getQuarterStartDate = (month: number, year: number) => {
    const quarterStartMonth = Math.floor(month / 3) * 3; // Calculate the first month of the quarter
    return new Date(year, quarterStartMonth, 15); // Return the 15th of the first month of the quarter
  };

  const isMonthPassed = (rowMonth: string) => {
    if (!rowMonth || !rowMonth.includes("-")) {
      return false; // Return false if rowMonth is undefined or not in the expected format
    }
    const [, month] = rowMonth.split("-").map(Number);
    if (isNaN(month)) {
      return false; // Return false if month is not a valid number
    }
    const quarterStartDate = getQuarterStartDate(month - 1, selectedYear);
    const currentDate = new Date();
    return currentDate > quarterStartDate;
  };

  const NonEditableMonths = useMemo(() => {
    return calculateNonEditableMonths(
      RequiredDataLastMonths,
      monthlyData,
      monthNames,
      selectedYear,
    );
  }, [RequiredDataLastMonths, monthlyData, monthNames, selectedYear]);

  const isMonthEditable = (monthName: string) => {
    const currentDate = new Date();
    const monthIndex = monthNames.indexOf(monthName);
    const currentYear = currentDate.getFullYear();

    // If selected year is future year, all months are editable
    if (Number(selectedYear) > currentYear) {
      return true;
    }

    // If selected year is past year, no months are editable
    if (Number(selectedYear) < currentYear) {
      return false;
    }

    // For current year, apply the quarter logic
    const currentQuarter = Math.floor(
      currentDate.getDate() > 15
        ? currentDate.getMonth() / 3
        : (currentDate.getMonth() - 1) / 3,
    );

    // Block all forecasts if we're in Q4 after 15th Oct of the selected year
    if (currentDate.getMonth() >= 9 && currentDate.getDate() > 15) {
      return false;
    }

    const lockedMonths = (currentQuarter + 1) * 3;
    return monthIndex >= lockedMonths;
  };

  // Function to calculate initial values for each month
  const calculateInitialValues = (
    month: string,
    monthlyDataParam?: { [key: string]: any },
  ) => {
    // Use passed monthlyData if available, otherwise use state
    const currentMonthlyData = monthlyDataParam || monthlyData;

    let previousMonthActiveUsers;
    const monthIndex = monthNames.indexOf(month);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();
    const selectedYearNum = Number(selectedYear);
    const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

    const monthNum = monthIndex + 1;
    const paddedMonth = monthNum.toString().padStart(2, "0");
    const monthStr = `${selectedYear}-${paddedMonth}`;
    const isPassed = isMonthPassed(monthStr);

    // Calculate previous month's active users with cascading effect
    if (isPassed) {
      previousMonthActiveUsers = lastMonthActiveUsers;
    } else {
      if (monthIndex === 0) {
        previousMonthActiveUsers = lastMonthActiveUsers;
      } else {
        const prevMonthName = monthNames[monthIndex - 1];
        const prevMonthStr = `${selectedYear}-${monthIndex
          .toString()
          .padStart(2, "0")}`;
        const isPrevMonthPassed = isMonthPassed(prevMonthStr);

        if (isPrevMonthPassed) {
          if (isEarlyQ3 && monthIndex === 6) {
            const juneData = LastData.find((entry: any) => {
              const [year, month] = entry.month.split("-").map(Number);
              return month === 6 && year === selectedYearNum;
            });
            previousMonthActiveUsers =
              juneData?.active_users || lastMonthActiveUsers;
          } else {
            previousMonthActiveUsers = lastMonthActiveUsers;
          }
        } else {
          // Use the previously calculated month's active users
          const prevMonthData = currentMonthlyData[prevMonthName];
          previousMonthActiveUsers =
            prevMonthData?.activeUsers || lastMonthActiveUsers;
        }
      }
    }

    const averageGrowth = forecastRows[0]?.averageGrowth || 0;
    const averageChurnRate = forecastRows[0]?.averageChurnRate || 0;
    const averageRatioBetweenAcquiredToFtd =
      forecastRows[0]?.averageRatioAcquiredToFtd || 0;
    const averageNetRevPerActiveUser =
      forecastRows[0]?.averageAvgNetRevPerActiveUser; // corrency value
    const averageRsP = forecastRows[0]?.averageAvgRsP || 0;
    const averageRsBrNg = forecastRows[0]?.averageRsBrNg || 0;
    const averageCpaPv = forecastRows[0]?.averageCpaPv || 0; // currency value
    const averageCVR = forecastRows[0]?.averageCVR || 0;
    const averageProfit = forecastRows[0]?.averageProfit || 0;
    const averageFTD = forecastRows[0]?.averageFtd || 0;
    const averageCAC = forecastRows[0]?.averageCAC || 0;
    const oldActiveUsers = Math.round(
      (1 - averageChurnRate) * Math.round(previousMonthActiveUsers),
    );
    let ftd;
    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      ftd = averageFTD;
    } else {
      ftd = Math.round(
        ((1 / (1 - averageChurnRate) - 1) * oldActiveUsers +
          averageGrowth * Math.round(previousMonthActiveUsers)) /
          averageRatioBetweenAcquiredToFtd,
      );
    }
    const acquiredUsers = averageRatioBetweenAcquiredToFtd * ftd;
    const activeUsers = oldActiveUsers + acquiredUsers;
    const estNetRev = averageNetRevPerActiveUser * activeUsers * averageRsBrNg; // currency value
    const estRs = estNetRev * averageRsP; // currency value
    const rsPv = estRs / ftd; // currency value
    const estCpaIncome = averageCpaPv * ftd; // currency value
    const cvr = averageCVR;
    const totalIncome = estCpaIncome + estRs; // currency value
    let profit, spend, cac;
    if (selectedTemplateType === 1 || selectedTemplateType === 2) {
      profit = averageProfit;
      spend = totalIncome - profit;
      cac = spend / ftd;
    } else {
      cac = averageCAC;
      spend = cac * ftd;
      profit = totalIncome - spend;
    }
    let growth;
    if (selectedTemplateType === 1) {
      growth = averageGrowth;
    } else {
      growth = activeUsers / Math.round(previousMonthActiveUsers) - 1;
    }
    const clicks = Math.round(ftd / cvr);
    const epcCpa = estCpaIncome / clicks;
    const epcRs = estRs / clicks;
    const epc = epcCpa + epcRs;
    const cpc = spend / clicks;
    return {
      growth: growth,
      cpaPv: averageCpaPv,
      avgNetRevPerActiveUser: averageNetRevPerActiveUser,
      churnRate: averageChurnRate,
      ratioBetweenAcquiredToFtd: averageRatioBetweenAcquiredToFtd,
      avgRsP: averageRsP,
      rsBrToRsActiveRatio: averageRsBrNg,
      cvr: averageCVR,
      profit,
      ftd,
      oldActiveUsers,
      acquiredUsers,
      rsPv,
      estRs,
      estCpaIncome,
      clicks,
      cac,
      spend,
      totalIncome,
      epcCpa,
      epcRs,
      epc,
      cpc,
      activeUsers,
      estNetRev,
      nrpu: 0, // Default NRPU to 0
    };
  };

  // Determine which months are locked based on current date
  const determineLockedMonths = (date: any) => {
    const month = date.getMonth(); // 0-11
    const day = date.getDate();

    // Until January 15: All 12 months editable
    if (month === 0 && day <= 15) {
      return Array(12).fill(false);
    }
    // January 16 - April 15: First 3 months locked
    else if (
      (month === 0 && day > 15) ||
      (month >= 1 && month < 3) ||
      (month === 3 && day <= 15)
    ) {
      return [...Array(3).fill(true), ...Array(9).fill(false)];
    }
    // April 16 - July 15: First 6 months locked
    else if (
      (month === 3 && day > 15) ||
      (month >= 4 && month < 6) ||
      (month === 6 && day <= 15)
    ) {
      return [...Array(6).fill(true), ...Array(6).fill(false)];
    }
    // July 16 - October 15: First 9 months locked
    else if (
      (month === 6 && day > 15) ||
      (month >= 7 && month < 9) ||
      (month === 9 && day <= 15)
    ) {
      return [...Array(9).fill(true), ...Array(3).fill(false)];
    }
    // October 16 - December 31: All 12 months locked
    else {
      return Array(12).fill(true);
    }
  };

  // Calculate average metrics based on lookback periods
  const calculateAverages = (data: any) => {
    if (!data || data.length === 0) return {};
    const currentMonth = getMonthName(currentDate.getMonth());
    // Sort and filter data excluding 'RR' entries
    const actualData = data
      .filter(
        (item: any) =>
          !item.month.includes("RR") && item.displayMonth !== currentMonth,
      )
      .sort((a: any, b: any) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateB.getTime() - dateA.getTime();
      });

    // Calculate averages using the filtered data
    const averages = {
      averageGrowth: calculateAverage(actualData, "growth", 6),
      averageCVR: calculateAverage(actualData, "CVR", 6),
      averageAvgNetRevPerActiveUser: calculateAverage(
        actualData,
        "avg_net_rev_per_active_user",
        6,
      ),
      averageChurnRate: calculateAverage(actualData, "churn_rate", 3),
      averageRatioAcquiredToFtd: calculateAverage(
        actualData,
        "Ratio_between_acquired_to_ftd",
        3,
      ),
      averageAvgRsP: calculateAverage(actualData, "avg_rs_p", 3),
      averageRsBrNg: calculateAverage(actualData, "rs_br_ng", 3),
      averageProfit: calculateAverage(actualData, "profit", 3),
      averageFtd: calculateAverage(actualData, "ftd", 3),
      averageCAC: calculateAverage(actualData, "CAC", 3),
      averageSpend: calculateAverage(actualData, "spend", 3),
      averageCpaPv: actualData[0]?.cpa_pv || 0,
    };

    return averages;
  };
  // Helper function to calculate average of a specific metric
  const calculateAverage = (data: any, metric: string, monthCount: number) => {
    // Take only the specified number of months
    const relevantData = data.slice(0, monthCount);
    // Filter out null/undefined values but keep zeros
    const validValues = relevantData
      .map((item: any) => item[metric])
      .filter((value: any) => value !== null && value !== undefined);

    if (validValues.length === 0) return 0;
    const sum = validValues.reduce((acc: number, val: number) => acc + val, 0);
    return sum / validValues.filter((value: any) => value !== 0).length;
  };

  // Generate forecast rows with calculated averages
  useEffect(() => {
    setIsDataInitialized(false); // Reset initialization state when data changes

    if (!LastYearData) {
      return; // Wait for LastYearData to be available
    }

    if (LastYearData.length === 0) {
      createEmptyForecastRows();
      return;
    }

    createForecastRowsWithData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LastYearData?.length]); // Add LastYearData as dependency

  const createEmptyForecastRows = () => {
    const lockedMonths = getLockedMonths();
    const emptyAverages = {
      averageGrowth: 0,
      averageCVR: 0,
      averageAvgNetRevPerActiveUser: 0,
      averageChurnRate: 0,
      averageRatioAcquiredToFtd: 0,
      averageAvgRsP: 0,
      averageRsBrNg: 0,
      averageProfit: 0,
      averageFtd: 0,
      averageCAC: 0,
      averageSpend: 0,
      averageCpaPv: 0,
    };
    createAndSetRows(lockedMonths, emptyAverages);
  };

  const createForecastRowsWithData = () => {
    const lockedMonths = getLockedMonths();
    const averages = calculateAverages(LastYearData);
    createAndSetRows(lockedMonths, averages);
  };

  const getLockedMonths = () => {
    const currentYear = new Date().getFullYear();
    const selectedYearNum = Number(selectedYear);
    return selectedYearNum === currentYear
      ? determineLockedMonths(currentDate)
      : Array(12).fill(false);
  };

  const createAndSetRows = (
    lockedMonths: boolean[],
    averages: Record<string, any>,
  ) => {
    const rows = Array.from({ length: 12 }, (_, index) => ({
      displayMonth: getMonthName(index),
      isLocked: lockedMonths[index] ? "Yes" : "No",
      ...averages,
    }));

    setForecastRows(rows);
  };

  // Initialize monthly data when forecast rows are available (ONE-TIME INITIALIZATION)
  useEffect(() => {
    if (forecastRows.length > 0 && !isDataInitialized) {
      const editableRows = forecastRows.filter(
        (row: any) => row.isLocked === "No",
      );
      const initialData: { [key: string]: any } = {};

      // If there are editable rows, process them
      if (editableRows.length > 0) {
        editableRows.forEach((row: any) => {
          // Calculate values for each month sequentially
          const monthData = calculateInitialValues(
            row.displayMonth,
            initialData,
          );
          initialData[row.displayMonth] = {
            ...monthData,
            displayMonth: row.displayMonth,
          };
        });
      } else {
        // If all months are locked (e.g., after Oct 15), create empty structure to show non-editable months
        // This ensures that the condition Object.keys(monthlyData).length <= 0 doesn't block the table rendering
        // The actual data will be shown via NonEditableMonthsRows component
        const emptyData: { [key: string]: any } = {};
        monthNames.forEach((monthName) => {
          emptyData[monthName] = { displayMonth: monthName };
        });
        initialData.placeholder = { displayMonth: "placeholder" }; // Add a placeholder to avoid empty object
      }

      dispatch(setMonthlyData(initialData));
      setIsDataInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastRows]); // Only initialize once when forecastRows change

  // Recalculate all months when lastMonthActiveUsers changes (PRESERVES USER EDITS)
  useEffect(() => {
    if (
      isDataInitialized &&
      lastMonthActiveUsers !== null &&
      Object.keys(monthlyData).length > 0
    ) {
      // Recalculate all months while preserving user-edited values
      let updatedMonthlyData = { ...monthlyData };

      for (let i = 0; i < monthNames.length; i++) {
        const currentMonth = monthNames[i];
        if (
          updatedMonthlyData[currentMonth] &&
          currentMonth !== "placeholder"
        ) {
          if (i === 0) {
            // First month: recalculate with current values
            updatedMonthlyData[currentMonth] = calculateValues(
              currentMonth,
              updatedMonthlyData[currentMonth],
            );
          } else {
            // Subsequent months: use previous month's active users
            const prevMonth = monthNames[i - 1];
            const prevMonthActiveUsers =
              updatedMonthlyData[prevMonth]?.activeUsers;
            updatedMonthlyData[currentMonth] = calculateValues(
              currentMonth,
              updatedMonthlyData[currentMonth],
              prevMonthActiveUsers,
            );
          }
        }
      }

      dispatch(setMonthlyData(updatedMonthlyData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMonthActiveUsers]); // Recalculate when lastMonthActiveUsers changes

  // Function to calculate values based on changes
  const calculateValues = (
    month: string,
    newValues: any,
    forcePreviousMonthActiveUsers?: number,
  ) => {
    const data = { ...monthlyData[month], ...newValues };
    // Get the previous month's active users based on the month name
    let previousMonthActiveUsers;
    const monthIndex = monthNames.indexOf(month);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    // const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();
    const selectedYearNum = Number(selectedYear);

    // If forcePreviousMonthActiveUsers is provided, use that value
    if (forcePreviousMonthActiveUsers !== undefined) {
      previousMonthActiveUsers = forcePreviousMonthActiveUsers;
    } else {
      // Determine if we're in early Q3 (July 1-15)
      const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

      // Check if the month is passed according to isMonthPassed
      const monthNum = monthIndex + 1;
      const paddedMonth = monthNum.toString().padStart(2, "0");
      const monthStr = `${selectedYear}-${paddedMonth}`;
      const isPassed = isMonthPassed(monthStr);
      if (isPassed) {
        // For passed months, use the RR data
        previousMonthActiveUsers = lastMonthActiveUsers;
      } else {
        // For future months, use cascading calculation
        if (monthIndex === 0) {
          // January is the first month, use RR data
          previousMonthActiveUsers = lastMonthActiveUsers;
        } else {
          // For other months, use the previous month's calculated active_users
          const prevMonthName = monthNames[monthIndex - 1];

          // Check if previous month is passed or already calculated
          const prevMonthStr = `${selectedYear}-${monthIndex
            .toString()
            .padStart(2, "0")}`;
          const isPrevMonthPassed = isMonthPassed(prevMonthStr);

          if (isPrevMonthPassed) {
            // If previous month is passed, use data from LastYearData
            // For Scenario 2: If we're in early Q3 (July 1-15), use June's data from last 12 month forecast
            if (isEarlyQ3 && monthIndex === 6) {
              // July
              const juneData = LastData.find((entry: any) => {
                const [year, month] = entry.month.split("-").map(Number);
                return month === 6 && year === selectedYearNum; // June of selected year
              });
              previousMonthActiveUsers =
                juneData?.active_users || lastMonthActiveUsers;
            } else {
              previousMonthActiveUsers = lastMonthActiveUsers;
            }
          } else if (monthlyData[prevMonthName]) {
            // If previous month is already calculated, use its active_users
            previousMonthActiveUsers = monthlyData[prevMonthName].activeUsers;
          } else {
            // Fallback to RR data if previous month data is not available
            previousMonthActiveUsers = lastMonthActiveUsers;
          }
        }
      }
    }
    // Always recalculate oldActiveUsers based on the provided or calculated previousMonthActiveUsers
    data.oldActiveUsers = Math.round(
      (1 - data.churnRate) * Math.round(previousMonthActiveUsers),
    );

    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      // For templates 2 and 3, FTD is fixed and growth is calculated
      data.growth =
        (data.oldActiveUsers + data.acquiredUsers) /
          Math.round(previousMonthActiveUsers) -
        1;
    } else {
      // For template 1, growth is fixed and FTD is calculated
      data.ftd = Math.round(
        ((1 / (1 - data.churnRate) - 1) * data.oldActiveUsers +
          data.growth * Math.round(previousMonthActiveUsers)) /
          data.ratioBetweenAcquiredToFtd,
      );
    }

    // Recalculate all dependent values
    data.acquiredUsers = data.ratioBetweenAcquiredToFtd * data.ftd;
    data.activeUsers = data.oldActiveUsers + data.acquiredUsers;

    // Ensure growth is consistent with activeUsers change for all templates
    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      data.growth = data.activeUsers / Math.round(previousMonthActiveUsers) - 1;
    }

    data.estNetRev =
      data.avgNetRevPerActiveUser * data.activeUsers * data.rsBrToRsActiveRatio;
    data.estRs = data.estNetRev * data.avgRsP;
    data.rsPv = data.estRs / data.ftd;
    data.estCpaIncome = data.cpaPv * data.ftd;
    data.totalIncome = data.estCpaIncome + data.estRs;
    data.clicks = Math.round(data.ftd / data.cvr);

    if (selectedTemplateType === 1 || selectedTemplateType === 2) {
      // For templates 1 and 2, profit is fixed and spend/cac are calculated
      data.spend = data.totalIncome - data.profit;
      data.cac = data.spend / data.ftd;
    } else {
      // For template 3, CAC is fixed and profit/spend are calculated
      data.spend = data.cac * data.ftd;
      data.profit = data.totalIncome - data.spend;
    }

    data.epcRs = data.estRs / data.clicks;
    data.epcCpa = data.estCpaIncome / data.clicks;
    data.epc = data.epcCpa + data.epcRs;
    data.cpc = data.spend / data.clicks;
    data.nrpu = newValues.nrpu !== undefined ? newValues.nrpu : data.nrpu || 0;
    return data;
  };

  const { handleCopyToNext } = useForecastCopy({
    config: FTD_CONFIG,
    editableStates,
    calculateValues,
    monthNames,
  });

  // Handle input changes and recalculate values
  const handleInputChange = (month: string, field: string, value: number) => {
    let updatedMonthlyData = { ...monthlyData }; // Start with the changed month
    const monthIndex = monthNames.indexOf(month);
    updatedMonthlyData[month] = calculateValues(month, { [field]: value }); // Update the current month first

    for (let i = monthIndex + 1; i < monthNames.length; i++) {
      // Recalculate all subsequent months
      const nextMonth = monthNames[i];
      if (updatedMonthlyData[nextMonth]) {
        // Only update if the month exists in monthlyData
        const prevMonth = monthNames[i - 1]; // Get the previous month and its active users
        const prevMonthActiveUsers = updatedMonthlyData[prevMonth].activeUsers;
        updatedMonthlyData[nextMonth] = calculateValues(
          nextMonth,
          updatedMonthlyData[nextMonth],
          prevMonthActiveUsers,
        ); // Recalculate with the forced previous month's active users
      }
    }
    dispatch(setMonthlyData(updatedMonthlyData));
  };

  // Individual field change handlers
  const onGrowthChange = (e: any, month: string) => {
    handleInputChange(month, "growth", parseFloat(e.target.value));
  };

  const onRatioAcquiredToFtdChange = (e: any, month: string) => {
    handleInputChange(
      month,
      "ratioBetweenAcquiredToFtd",
      parseFloat(e.target.value),
    );
  };

  const onAvgNetRevPerActiveUserChange = (e: any, month: string) => {
    handleInputChange(
      month,
      "avgNetRevPerActiveUser",
      parseFloat(e.target.value),
    );
  };

  const onCpaPvChange = (e: any, month: string) => {
    handleInputChange(month, "cpaPv", parseFloat(e.target.value));
  };

  const onCvrChange = (e: any, month: string) => {
    handleInputChange(month, "cvr", parseFloat(e.target.value));
  };

  const onProfitChange = (e: any, month: string) => {
    handleInputChange(month, "profit", parseFloat(e.target.value));
  };

  const onAvgRsPChange = (e: any, month: string) => {
    handleInputChange(month, "avgRsP", parseFloat(e.target.value));
  };

  const onRsBrToRsActiveRatio = (e: any, month: string) => {
    handleInputChange(month, "rsBrToRsActiveRatio", parseFloat(e.target.value));
  };

  const onChurnRateChange = (e: any, month: string) => {
    handleInputChange(month, "churnRate", parseFloat(e.target.value));
  };

  const onNrpuChange = (e: any, month: string) => {
    handleInputChange(month, "nrpu", parseFloat(e.target.value) || 0);
  };

  const tableheaders = useMemo(
    () => [
      { id: 0, KEY: "month", label: "Month", sortable: true },
      { id: 1, KEY: "nrpu", label: "NRPU", sortable: true },
      { id: 2, KEY: "growth", label: "Growth Target", sortable: true },
      {
        id: 3,
        KEY: "avg_net_rev_per_active_user",
        label: "Avg Net Rev To Active Users",
        sortable: true,
      },
      { id: 4, KEY: "cpa_pv", label: "CPA PV", sortable: true },
      { id: 5, KEY: "CVR", label: "CVR", sortable: true },
      { id: 6, KEY: "profit", label: "Profit", sortable: true },
      { id: 7, KEY: "active_users", label: "Active Users", sortable: true },
      { id: 8, KEY: "old", label: "Old Active Users", sortable: true },
      { id: 9, KEY: "aquired", label: "Aquired Users", sortable: true },
      { id: 10, KEY: "ftd", label: "FTD", sortable: true },
      { id: 11, KEY: "net_rev", label: "Net Revenue", sortable: true },
      { id: 12, KEY: "rs_income_dollar", label: "RS", sortable: true },
      { id: 13, KEY: "rs_pv", label: "RS PV", sortable: true },
      { id: 14, KEY: "cpa_income_dollar", label: "CPA Income", sortable: true },
      { id: 15, KEY: "total_income", label: "Total Income", sortable: true },
      { id: 16, KEY: "clicks", label: "Clicks", sortable: true },
      { id: 17, KEY: "EPC_CPA", label: "EPC CPA", sortable: true },
      { id: 18, KEY: "EPC_RS", label: "EPC RS", sortable: true },
      { id: 19, KEY: "EPC", label: "EPC", sortable: true },
      { id: 20, KEY: "CPC", label: "CPC", sortable: true },
      { id: 21, KEY: "CAC", label: "CAC", sortable: true },
      { id: 22, KEY: "spend", label: "Spend", sortable: true },
      { id: 23, KEY: "avg_rs_p", label: "Avg RS Percent", sortable: true },
      { id: 24, KEY: "churn_rate", label: "Churn Rate", sortable: true },
      {
        id: 25,
        KEY: "rs_br_ng",
        label: "RS BR to NG Active Percent",
        sortable: true,
      },
      {
        id: 26,
        KEY: "Ratio_between_acquired_to_ftd",
        label: "Aquired to FTD",
        sortable: true,
      },
    ],
    [],
  );

  const handleCreateForecast = async (status: "draft" | "publish") => {
    try {
      setLoading(true);
      // Transform forecastRows into the desired payload structure
      const forecastData = Object.entries(monthlyData)
        .filter(([month]) => isMonthEditable(month))
        .map(([month, data]) => {
          const monthIndex = monthNames.indexOf(month) + 1;
          const paddedMonth = monthIndex.toString().padStart(2, "0");
          const typedData = data as any;
          return {
            month: `${selectedYear}-${paddedMonth}-01`,
            spend: typedData.spend,
            clicks: typedData.clicks,
            cpc: typedData.cpc,
            ftd: Number(typedData.ftd.toFixed(0)),
            cvr: Number(typedData.ftd.toFixed(0)) / typedData.clicks,
            cac: typedData.cac,
            profit: typedData.profit,
            cpa: typedData.estCpaIncome,
            rs: typedData.estRs,
            total_income: typedData.totalIncome,
            cpa_pv: typedData.cpaPv,
            rs_pv: typedData.rsPv,
            pv: typedData.cpaPv + typedData.rsPv,
            growth_target: typedData.growth,
            active_users: typedData.activeUsers,
            old_active_users: typedData.oldActiveUsers,
            acquired_active_users: typedData.acquiredUsers,
            churn_rate: typedData.churnRate,
            ratio_between_acquired_to_ftd: typedData.ratioBetweenAcquiredToFtd,
            avg_net_rev_per_active_user: typedData.avgNetRevPerActiveUser,
            net_revenue: typedData.estNetRev,
            avg_rs_deal_p: typedData.avgRsP,
            br_to_ng_active_rs_ratio: typedData.rsBrToRsActiveRatio,
            epc_cpa: typedData.epcCpa,
            epc_rs: typedData.epcRs,
            epc: typedData.epc,
            nrpu: typedData.nrpu || 0,
          };
        });
      const currentDate = new Date(); // Block forecast creation if in Q4 after 15th Oct
      const currentYear = currentDate.getFullYear();
      if (
        Number(selectedYear) === currentYear &&
        ((currentDate.getMonth() === 9 && currentDate.getDate() > 15) ||
          currentDate.getMonth() > 9)
      ) {
        setLoading(false);
        store.dispatch(
          setDisplayErrorSnackBar({
            message:
              "Forecast creation for Q4 is closed after 15th October. You can no longer create or modify forecasts for this period.",
            type: "info",
          }),
        );
        return;
      }

      if (!oauthTokenHeader) {
        throw new Error("User doesn't have permissions to create new forecast");
      }

      const response = await forecastersEndPointLogic.createNewForecast(
        oauthTokenHeader,
        {
          year: Number(selectedYear),
          template_type: selectedTemplateType,
          status: status,
          notes: forecastNote,
          forecaster_content: forecastData,
          last_month_active_users: Number(lastMonthActiveUsers),
        },
        selectedSite,
      );

      if (response?.status === 201) {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Forecast Has Been Created Successfully",
            type: "success",
          }),
        );
        navigate(
          `/business_leader/forecaster?${new URLSearchParams(
            location.search,
          ).toString()}`,
        );
        dispatch(setForecastStatus(null));
        dispatch(setForecastNote(""));
        dispatch(setAllReviewed(false));
        dispatch(setSelectedTemplateType(null));
      }
    } catch (error: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: error?.message || "Error creating forecast",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (forecastStatus) {
      handleCreateForecast(forecastStatus);
      dispatch(setForecastStatus(null));
    }
    // eslint-disable-next-line
  }, [forecastStatus]);

  const formatNumber = (value: any) => {
    if (!value || value === Infinity || value === -Infinity || isNaN(value))
      return "0";
    const roundedValue = Number(Math.round(value));
    return roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const [openMetrics, setOpenMetrics] = useState(false);

  const handleClickOpen = (metricKey: string) => {
    setOpenMetrics(true);
    dispatch(setSelectedMetric(metricKey));
    const currentMonth = new Date().getMonth(); // 0–11
    const month = String(currentMonth + 1).padStart(2, "0");
    const formattedMonth = `${selectedYear}-${month}-01`;
    dispatch(setSelectedMonth(formattedMonth));
    if (selectedSite) {
      dispatch(setSelectedSite(selectedSite));
    } else {
      store.dispatch(
        setDisplayErrorSnackBar(
          "Please select a site. No selected site found in the URL",
        ),
      );
    }
  };

  const handleClose = () => {
    setOpenMetrics(false);
  };

  //(R) for the first month of the current quarter (if not passed) (F) for all other future months
  const getMonthIndicator = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    if (year === currentYear && (month - 1) % 3 === 0) {
      return "(R)";
    } else {
      return "(F)";
    }
  };

  // Sticky header logic
  const leftStickyIds = useMemo(
    () => new Set<number>([0, 1, 2, 3, 4, 5, 6]),
    [],
  );
  // const rightStickyIds = useMemo(() => new Set([]), []);
  const rightStickyIds = useMemo(() => new Set<number>([]), []);

  const headerCellRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  const assignedCountRef = useRef(0);
  // whenever header count changes, ensure the ref array has that length
  useEffect(() => {
    headerCellRefs.current = Array(tableheaders.length).fill(null);
    assignedCountRef.current = 0;
  }, [tableheaders.length]);

  // state to hold computed offsets for left and right sticky cells
  const [computedLeftOffsets, setComputedLeftOffsets] = useState<
    Record<number, number>
  >({});
  const [computedRightOffsets, setComputedRightOffsets] = useState<
    Record<number, number>
  >({});

  // callbackRef factory — used as ref prop on TableCell
  const makeHeaderRef =
    (index: number) => (el: HTMLTableCellElement | null) => {
      // assign element (can be null when unmounting)
      const prev = headerCellRefs.current[index];
      headerCellRefs.current[index] = el;

      // if element newly attached, increment assigned count
      if (!prev && el) {
        assignedCountRef.current++;
      }

      // if element removed, decrement (keeps count accurate on re-renders)
      if (prev && !el) {
        assignedCountRef.current = Math.max(0, assignedCountRef.current - 1);
      }
    };

  // shallow comparison helper — only compare own-number-valued props (fast)
  const offsetsEqual = (
    a: Record<number, number>,
    b: Record<number, number>,
  ) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if ((a as any)[k] !== (b as any)[k]) return false;
    }
    return true;
  };

  // measure widths and compute cumulative left/right offsets
  useLayoutEffect(() => {
    let mounted = true;
    let ro: ResizeObserver | null = null;

    const computeOffsets = () => {
      if (!mounted) return;
      const leftOffsets: Record<number, number> = {};
      let cumLeft = 0;
      for (let i = 0; i < tableheaders.length; i++) {
        if (leftStickyIds.has(i)) {
          leftOffsets[i] = cumLeft;
          const el = headerCellRefs.current[i];
          const w = el ? el.getBoundingClientRect().width : i === 0 ? 115 : 0;
          cumLeft += w;
        }
      }

      const rightOffsets: Record<number, number> = {};
      let cumRight = 0;
      const rightIds = Array.from(rightStickyIds).sort((a, b) => b - a);
      for (const id of rightIds) {
        rightOffsets[id] = cumRight;
        const el = headerCellRefs.current[id];
        const w = el ? el.getBoundingClientRect().width : 115;
        cumRight += w;
      }

      setComputedLeftOffsets((prev) => {
        if (!mounted) return prev;
        if (offsetsEqual(prev || {}, leftOffsets)) return prev;
        return leftOffsets;
      });

      setComputedRightOffsets((prev) => {
        if (!mounted) return prev;
        if (offsetsEqual(prev || {}, rightOffsets)) return prev;
        return rightOffsets;
      });
    };

    const scheduleFinalMeasure = () => {
      // prefer fonts.ready when available, then double-RaF to ensure layout is final
      if ((document as any).fonts && (document as any).fonts.ready) {
        (document as any).fonts.ready.then(() => {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => mounted && computeOffsets()),
          );
        });
      } else {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => mounted && computeOffsets()),
        );
      }
    };

    // If header refs aren't all assigned yet, wait for them.
    // Otherwise run immediate measurement (and schedule the stable re-measure).
    const tryInitialMeasure = () => {
      const assigned = assignedCountRef.current;
      if (assigned < tableheaders.length) {
        // not all refs attached yet — defer until they are attached.
        // Poll briefly until assignedCount matches the header count (fast and stops rapidly).
        const check = () => {
          if (!mounted) return;
          if (assignedCountRef.current >= tableheaders.length) {
            computeOffsets(); // one immediate compute
            scheduleFinalMeasure();
          } else {
            // check again next frame (small, bounded polling)
            requestAnimationFrame(check);
          }
        };
        requestAnimationFrame(check);
      } else {
        // all refs available right now
        computeOffsets();
        scheduleFinalMeasure();
      }
    };

    // Start: try to measure once refs are present
    tryInitialMeasure();

    // Keep ResizeObserver for future changes, schedule via rAF
    const tableEl = document.querySelector(".FTDForecasterTable table");
    if (tableEl) {
      ro = new ResizeObserver(() =>
        requestAnimationFrame(() => mounted && computeOffsets()),
      );
      ro.observe(tableEl);
    }
    window.addEventListener("resize", computeOffsets);

    return () => {
      mounted = false;
      if (ro) ro.disconnect();
      window.removeEventListener("resize", computeOffsets);
    };
  }, [tableheaders, leftStickyIds, rightStickyIds, loading, isDataInitialized]);

  //for scrolling from 6th profit column
  const FIXED_LAST_INDEX = 6;

  const fixedColumnsWidth = useMemo(() => {
    const offset = computedLeftOffsets[FIXED_LAST_INDEX] ?? 0;
    const el = headerCellRefs.current[FIXED_LAST_INDEX];
    const width = el ? el.getBoundingClientRect().width : 0;
    return offset + width;
  }, [computedLeftOffsets]);

  const tableScrollRef = useRef<HTMLDivElement>(null);

  return (
    <>
      {loading || !isDataInitialized ? (
        <ForecastSkeleton />
      ) : (
        <>
          <Paper elevation={0}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                padding: "8px 16px",
                position: "absolute",
                top: 64,
                right: 0,
              }}
            >
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      color="secondary"
                      data-tracker="522"
                      checked={showOnlyForecasts}
                      onChange={(e) => setShowOnlyForecasts(e.target.checked)}
                    />
                  }
                  label="Show only forecasts"
                  labelPlacement="start"
                />
              </Box>
            </Box>

            <InfoCard
              isEditing={Object.values(editableStates).some(
                (val) => val !== null,
              )}
              editableStates={editableStates}
              config={FTD_CONFIG}
              onCopyToNext={handleCopyToNext}
              selectedFieldName={selectedFieldInfo?.name}
              fieldType={selectedFieldInfo?.type}
              valueSourceText={selectedFieldInfo?.valueText}
              impactText={selectedFieldInfo?.impactText}
            />
            <TableContainer className="FTDForecasterTable" ref={tableScrollRef}>
              <Table aria-label="collapsible table" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {tableheaders.map((header) => {
                      // Decide which columns should be left-sticky
                      const getCellSx = (headerId: number): SxProps<Theme> => {
                        const base: SxProps<Theme> = {
                          color: "#000000",
                          padding:
                            headerId === 0
                              ? "0px 8px 0px 16px!important"
                              : "0px 8px !important",
                          boxSizing: "border-box",
                          backgroundColor: "#fff",
                          top: 0,
                          borderCollapse: "separate",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        };

                        if (leftStickyIds.has(headerId)) {
                          return {
                            ...base,
                            position: "sticky",
                            left: computedLeftOffsets[headerId] ?? 0,
                            zIndex: 99,
                            backgroundColor: "#ffffff",
                            borderRight:
                              headerId === 6
                                ? "1px solid #e0e0e0 !important"
                                : undefined,
                            color: headerId === 0 ? "#000000" : "#9C27B0",
                          };
                        }

                        if (rightStickyIds.has(headerId)) {
                          return {
                            ...base,
                            position: "sticky",
                            right: computedRightOffsets[headerId],
                            zIndex: 110,
                            backgroundColor: "#ffffff",
                          };
                        }

                        return base;
                      };
                      return (
                        <TableCell
                          key={header.id}
                          align="left"
                          sx={getCellSx(header.id)}
                          ref={makeHeaderRef(header.id)}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-start"
                            spacing={1}
                          >
                            {header.label !== "Month" &&
                            header.label !== "More" ? (
                              <IconButton
                                color="secondary"
                                size="small"
                                data-tracker="131"
                                onClick={() => handleClickOpen(`${header.KEY}`)}
                              >
                                <HistoryIcon
                                  sx={{ fontSize: "1rem" }}
                                  color="secondary"
                                />
                              </IconButton>
                            ) : null}
                            {
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  lineHeight: "24px",
                                  letterSpacing: "0.17px",
                                }}
                              >
                                {header.label}
                              </Typography>
                            }
                          </Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    <NonEditableFTD
                      MainLoading={loading}
                      showOnlyForecasts={showOnlyForecasts}
                      NonEditableMonths={NonEditableMonths}
                      RequiredPublishedMonthData={RequiredPublishedMonthData}
                      isMonthPassed={isMonthPassed}
                      formatNumber={formatNumber}
                      computedLeftOffsets={computedLeftOffsets}
                      computedRightOffsets={computedRightOffsets}
                      handleCellClick={handleCellClick}
                      isHighlighted={isHighlighted}
                      selectedCell={selectedCell}
                      onQuarterTotalsCalculated={handleNonEditableQuarterTotals}
                    />
                  }
                  {Object.values(monthlyData)
                    .filter((row: any) => row.displayMonth !== "placeholder")
                    .map((row: any, index) => {
                      const isQuarterEnd = [
                        "March",
                        "June",
                        "September",
                        "December",
                      ].includes(row.displayMonth);

                      // Calculate quarter totals if this is a quarter end
                      let quarterTotals: any = null;
                      if (isQuarterEnd) {
                        const allMonths = Object.values(monthlyData).filter(
                          (m: any) => m.displayMonth !== "placeholder",
                        );
                        const quarterMonths = allMonths.slice(
                          Math.max(0, index - 2),
                          index + 1,
                        );

                        quarterTotals = {
                          ftd: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.ftd || 0),
                            0,
                          ),
                          profit: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.profit || 0),
                            0,
                          ),
                          growth: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.growth || 0),
                            0,
                          ),
                          estNetRev: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.estNetRev || 0),
                            0,
                          ),
                          estRs: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.estRs || 0),
                            0,
                          ),
                          estCpaIncome: quarterMonths.reduce(
                            (sum: number, m: any) =>
                              sum + (m.estCpaIncome || 0),
                            0,
                          ),
                          totalIncome: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.totalIncome || 0),
                            0,
                          ),
                          spend: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.spend || 0),
                            0,
                          ),
                          cac: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.cac || 0),
                            0,
                          ),
                          clicks: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.clicks || 0),
                            0,
                          ),
                        };
                      }
                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            sx={{
                              backgroundColor: isMonthPassed(row.displayMonth)
                                ? "#EBEBEB"
                                : "inherit",
                              "& > *": {
                                borderBottom:
                                  "1px solid rgba(224, 224, 224, 1) !important",
                              },
                              height: 35,
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                backgroundColor: "#FFFFFF",
                                padding: "0px 8px 0px 16px !important",
                                position: "sticky",
                                left: computedLeftOffsets[0] ?? 0,
                                zIndex: 2,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                              >
                                <Typography variant="body2">
                                  {getMonthIndicator(row.displayMonth)}
                                </Typography>
                                {row.displayMonth ? row.displayMonth : ""}
                              </Stack>
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.NRPU,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[1],
                                zIndex: 3,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.NRPU,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#fff",
                              }}
                            >
                              <div
                                onClick={() =>
                                  setEditableNRPU(row.displayMonth)
                                }
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: computedLeftOffsets[1],

                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableNRPU === row.displayMonth}
                                  color="secondary"
                                  {...(editableNRPU === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]?.nrpu ||
                                          0,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]?.nrpu ||
                                          0,
                                      })}
                                  onValueChange={(values) => {
                                    onNrpuChange(
                                      {
                                        target: {
                                          value: values.floatValue ?? 0,
                                        },
                                      },
                                      row.displayMonth,
                                    );
                                  }}
                                  suffix="$"
                                  onBlur={() => setEditableNRPU(null)}
                                  thousandSeparator=","
                                  inputProps={{
                                    readOnly: editableNRPU !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s",
                                      ...(editableNRPU === row.displayMonth && {
                                        boxShadow:
                                          "0 2px 4px -1px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableNRPU === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableNRPU === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableNRPU === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isHighlighted(
                                          FIELD_NAMES.NRPU,
                                          row.displayMonth,
                                        )
                                          ? "#F3E5F5"
                                          : "#FFFFFF",
                                        cursor:
                                          editableNRPU === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[2],
                                zIndex: 3,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.GROWTH,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#fff",
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.GROWTH,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() =>
                                  setEditableGrowthTarget(row.displayMonth)
                                }
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: computedLeftOffsets[2],
                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={
                                    editableGrowthTarget === row.displayMonth
                                  }
                                  color="secondary"
                                  {...(editableGrowthTarget === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]
                                            ?.growth * 100,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]
                                            ?.growth * 100,
                                      })}
                                  onValueChange={(values) => {
                                    onGrowthChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth,
                                    );
                                  }}
                                  onBlur={() => setEditableGrowthTarget(null)}
                                  thousandSeparator=","
                                  suffix="%"
                                  inputProps={{
                                    readOnly:
                                      editableGrowthTarget !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s",
                                      ...(editableGrowthTarget ===
                                        row.displayMonth && {
                                        boxShadow:
                                          "0 2px 4px -1px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableGrowthTarget ===
                                          row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableGrowthTarget ===
                                          row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableGrowthTarget ===
                                          row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isHighlighted(
                                          FIELD_NAMES.GROWTH,
                                          row.displayMonth,
                                        )
                                          ? "#F3E5F5"
                                          : "#FFFFFF",
                                        cursor:
                                          editableGrowthTarget ===
                                          row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[3],
                                zIndex: 4,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.AVG_NET_REV,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#fff",
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.AVG_NET_REV,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() =>
                                  setEditableAvgNetREv(row.displayMonth)
                                }
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: computedLeftOffsets[3],
                                  zIndex: 4,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={
                                    editableAvgNetREv === row.displayMonth
                                  }
                                  color="secondary"
                                  {...(editableAvgNetREv === row.displayMonth
                                    ? {
                                        defaultValue: `${Number(monthlyData[row.displayMonth]?.avgNetRevPerActiveUser).toFixed(2)}$`,
                                      }
                                    : {
                                        value: `${Number(monthlyData[row.displayMonth]?.avgNetRevPerActiveUser).toFixed(2)}$`,
                                      })}
                                  decimalScale={2}
                                  onValueChange={(values) => {
                                    onAvgNetRevPerActiveUserChange(
                                      { target: { value: values.floatValue } },
                                      row.displayMonth,
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableAvgNetREv(null)}
                                  inputProps={{
                                    readOnly:
                                      editableAvgNetREv !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  suffix="$"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableAvgNetREv ===
                                        row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableAvgNetREv === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableAvgNetREv === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableAvgNetREv === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isHighlighted(
                                          FIELD_NAMES.AVG_NET_REV,
                                          row.displayMonth,
                                        )
                                          ? "#F3E5F5"
                                          : "#FFFFFF",
                                        cursor:
                                          editableAvgNetREv === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                        textAlign: "center",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[4],
                                zIndex: 5,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CPA_PV,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#fff",
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CPA_PV,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() =>
                                  setEditableCPAPV(row.displayMonth)
                                }
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: computedLeftOffsets[4],
                                  zIndex: 5,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableCPAPV === row.displayMonth}
                                  color="secondary"
                                  {...(editableCPAPV === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]
                                            ?.cpaPv || 0,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]
                                            ?.cpaPv || 0,
                                      })}
                                  decimalScale={editableCPAPV ? 2 : 0}
                                  onValueChange={(values) => {
                                    onCpaPvChange(
                                      { target: { value: values.floatValue } },
                                      row.displayMonth,
                                    );
                                  }}
                                  onBlur={() => setEditableCPAPV(null)}
                                  thousandSeparator=","
                                  suffix="$"
                                  inputProps={{
                                    readOnly:
                                      editableCPAPV !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableCPAPV ===
                                        row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableCPAPV === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableCPAPV === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableCPAPV === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isHighlighted(
                                          FIELD_NAMES.CPA_PV,
                                          row.displayMonth,
                                        )
                                          ? "#F3E5F5"
                                          : "#FFFFFF",
                                        cursor:
                                          editableCPAPV === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[5],
                                zIndex: 3,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CVR,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#fff",
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CVR,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() => setEditableCVR(row.displayMonth)}
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: computedLeftOffsets[5],
                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableCVR === row.displayMonth}
                                  color="secondary"
                                  {...(editableCVR === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]?.cvr *
                                          100,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]?.cvr *
                                          100,
                                      })}
                                  onValueChange={(values) => {
                                    onCvrChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth,
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableCVR(null)}
                                  inputProps={{
                                    readOnly: editableCVR !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableCVR === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableCVR === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableCVR === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableCVR === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isHighlighted(
                                          FIELD_NAMES.CVR,
                                          row.displayMonth,
                                        )
                                          ? "#F3E5F5"
                                          : "#FFFFFF",
                                        cursor:
                                          editableCVR === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[6],
                                zIndex: 3,
                                borderRight: "1px solid #e0e0e0 !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.PROFIT,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#fff",
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.PROFIT,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() =>
                                  setEditableProfit(row.displayMonth)
                                }
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: computedLeftOffsets[6],
                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableProfit === row.displayMonth}
                                  color="secondary"
                                  {...(editableProfit === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]?.profit,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]?.profit,
                                      })}
                                  decimalScale={editableProfit ? 2 : 0}
                                  onValueChange={(values) => {
                                    onProfitChange(
                                      { target: { value: values.floatValue } },
                                      row.displayMonth,
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableProfit(null)}
                                  inputProps={{
                                    readOnly:
                                      editableProfit !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  suffix="$"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableProfit ===
                                        row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableProfit === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableProfit === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableProfit === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isHighlighted(
                                          FIELD_NAMES.PROFIT,
                                          row.displayMonth,
                                        )
                                          ? "#F3E5F5"
                                          : "#FFFFFF",
                                        cursor:
                                          editableProfit === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.ACTIVE_USERS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.ACTIVE_USERS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ACTIVE_USERS,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.activeUsers ||
                                  row.activeUsers,
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.OLD_USERS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.OLD_USERS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.OLD_USERS,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.oldActiveUsers ||
                                  row.oldActiveUsers,
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.ACQUIRED_USERS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.ACQUIRED_USERS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ACQUIRED_USERS,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.acquiredUsers ||
                                  row.acquiredUsers,
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.FTD,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.FTD &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.FTD,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.ftd || row.ftd,
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.NET_REVENUE,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.NET_REVENUE &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.NET_REVENUE,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.estNetRev ||
                                  row.estNetRev,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.ESTIMATED_REVENUE_SHARE,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.ESTIMATED_REVENUE_SHARE &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ESTIMATED_REVENUE_SHARE,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.estRs ||
                                  row.rsIncomeDollar,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.RS_PV,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.RS_PV &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.RS_PV,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.rsPv || row.rsPV,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CPA_INCOME,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.CPA_INCOME &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CPA_INCOME,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.estCpaIncome ||
                                  row.cpaIncomeDollar,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.TOTAL_INCOME,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.TOTAL_INCOME &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.TOTAL_INCOME,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.totalIncome ||
                                  row.totalIncome,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CLICKS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.CLICKS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CLICKS,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.clicks ||
                                  row.clicks,
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.EPC_CPA,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.EPC_CPA &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.EPC_CPA,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.epcCpa ||
                                  row.epcCpa,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.EPC_RS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.EPC_RS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.EPC_RS,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.epcRs ||
                                  row.epcRs,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.EPC,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.EPC &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.EPC,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.epc || row.epc,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CPC,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.CPC &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CPC,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.cpc || row.cpc,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CAC,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.CAC &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CAC,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.cac || row.CAC,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.SPEND,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.SPEND &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.SPEND,
                                  row.displayMonth,
                                )
                              }
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.spend ||
                                  row.spend,
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month)
                                  ? "#F5F5F5"
                                  : isHighlighted(
                                        FIELD_NAMES.AVG_RS_P,
                                        row.displayMonth,
                                      )
                                    ? "#F3E5F5"
                                    : "#FFFFFF",
                                padding: "0px 8px !important",
                                position: "sticky",
                                right: computedRightOffsets[23],
                                zIndex: 2,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.AVG_RS_P,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() => setEditableRsP(row.displayMonth)}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableRsP === row.displayMonth}
                                  color="secondary"
                                  {...(editableRsP === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]
                                            ?.avgRsP * 100,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]
                                            ?.avgRsP * 100,
                                      })}
                                  onValueChange={(values) => {
                                    onAvgRsPChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth,
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableRsP(null)}
                                  inputProps={{
                                    readOnly: editableRsP !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableRsP === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableRsP === row.displayMonth
                                            ? "1px solid #9C27B0 !important"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableRsP === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableRsP === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isMonthPassed(
                                          row.month,
                                        )
                                          ? "#F5F5F5"
                                          : isHighlighted(
                                                FIELD_NAMES.AVG_RS_P,
                                                row.displayMonth,
                                              )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                        cursor:
                                          editableRsP === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>

                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month)
                                  ? "#F5F5F5"
                                  : isHighlighted(
                                        FIELD_NAMES.CHURN_RATE,
                                        row.displayMonth,
                                      )
                                    ? "#F3E5F5"
                                    : "#FFFFFF",
                                padding: "0px 8px !important",
                                position: "sticky",
                                right: computedRightOffsets[24],
                                zIndex: 2,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CHURN_RATE,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() =>
                                  setEditableChurnRate(row.displayMonth)
                                }
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={
                                    editableChurnRate === row.displayMonth
                                  }
                                  color="secondary"
                                  {...(editableChurnRate === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]
                                            ?.churnRate * 100,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]
                                            ?.churnRate * 100,
                                      })}
                                  onValueChange={(values) => {
                                    onChurnRateChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth,
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableChurnRate(null)}
                                  inputProps={{
                                    readOnly:
                                      editableChurnRate !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableChurnRate ===
                                        row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableChurnRate === row.displayMonth
                                            ? "1px solid #9C27B0 !important"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableChurnRate === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableChurnRate === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isMonthPassed(
                                          row.month,
                                        )
                                          ? "#F5F5F5"
                                          : isHighlighted(
                                                FIELD_NAMES.CHURN_RATE,
                                                row.displayMonth,
                                              )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                        cursor:
                                          editableChurnRate === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month)
                                  ? "#F5F5F5"
                                  : isHighlighted(
                                        FIELD_NAMES.RS_BR_NG,
                                        row.displayMonth,
                                      )
                                    ? "#F3E5F5"
                                    : "#FFFFFF",
                                padding: "0px 8px !important",
                                position: "sticky",
                                right: computedRightOffsets[25],
                                zIndex: 2,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.RS_BR_NG,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() =>
                                  setEditableRsBrNg(row.displayMonth)
                                }
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableRsBrNg === row.displayMonth}
                                  color="secondary"
                                  {...(editableRsBrNg === row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]
                                            ?.rsBrToRsActiveRatio * 100,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]
                                            ?.rsBrToRsActiveRatio * 100,
                                      })}
                                  onValueChange={(values) => {
                                    onRsBrToRsActiveRatio(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth,
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableRsBrNg(null)}
                                  inputProps={{
                                    readOnly:
                                      editableRsBrNg !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableRsBrNg ===
                                        row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableRsBrNg === row.displayMonth
                                            ? ""
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableRsBrNg === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableRsBrNg === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isMonthPassed(
                                          row.month,
                                        )
                                          ? "#F5F5F5"
                                          : isHighlighted(
                                                FIELD_NAMES.RS_BR_NG,
                                                row.displayMonth,
                                              )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                        cursor:
                                          editableRsBrNg === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month)
                                  ? "#F5F5F5"
                                  : isHighlighted(
                                        FIELD_NAMES.ACQUIRED_TO_FTD,
                                        row.displayMonth,
                                      )
                                    ? "#F3E5F5"
                                    : "#FFFFFF",
                                padding: "0px 8px !important",
                                position: "sticky",
                                right: computedRightOffsets[26],
                                zIndex: 2,
                              }}
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ACQUIRED_TO_FTD,
                                  row.displayMonth,
                                )
                              }
                            >
                              <div
                                onClick={() =>
                                  setEditableAcquiredToFtd(row.displayMonth)
                                }
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={
                                    editableAcquiredToFtd === row.displayMonth
                                  }
                                  color="secondary"
                                  {...(editableAcquiredToFtd ===
                                  row.displayMonth
                                    ? {
                                        defaultValue:
                                          monthlyData[row.displayMonth]
                                            ?.ratioBetweenAcquiredToFtd * 100,
                                      }
                                    : {
                                        value:
                                          monthlyData[row.displayMonth]
                                            ?.ratioBetweenAcquiredToFtd * 100,
                                      })}
                                  onValueChange={(values) => {
                                    onRatioAcquiredToFtdChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth,
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableAcquiredToFtd(null)}
                                  inputProps={{
                                    readOnly:
                                      editableAcquiredToFtd !==
                                      row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableAcquiredToFtd ===
                                        row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableAcquiredToFtd ===
                                          row.displayMonth
                                            ? "1px solid #9C27B0 !important"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableAcquiredToFtd ===
                                          row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableAcquiredToFtd ===
                                          row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor: isMonthPassed(
                                          row.month,
                                        )
                                          ? "#F5F5F5"
                                          : isHighlighted(
                                                FIELD_NAMES.ACQUIRED_TO_FTD,
                                                row.displayMonth,
                                              )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                        cursor:
                                          editableAcquiredToFtd ===
                                          row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                          {isQuarterEnd && quarterTotals && (
                            <TableRow
                              sx={{
                                backgroundColor: "transparent",
                                "& > *": {
                                  borderBottom: "2px solid #E7E7E7 !important",
                                },
                                height: 35,
                              }}
                            >
                              <QuarterSummaryCell
                                value={`Q${Math.ceil((monthNames.indexOf(row.displayMonth) + 1) / 3)} Total`}
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[0] ?? 0}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[1]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[2]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[3]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[4]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[5]}
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.profit)}$`}
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[6]}
                                hasBorder
                              />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell
                                value={formatNumber(quarterTotals.ftd)}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.estNetRev)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.estRs)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.estCpaIncome)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.totalIncome)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.clicks)}`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.spend)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[23]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[24]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[25]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[26]}
                              />
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  {/* Year Total Row - Combines data from both components */}
                  {(() => {
                    // Calculate totals from NewForecastForFTD months
                    const allMonths = Object.values(monthlyData).filter(
                      (m: any) => m.displayMonth !== "placeholder",
                    );

                    const editableYearTotals = {
                      ftd: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.ftd || 0),
                        0,
                      ),
                      profit: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.profit || 0),
                        0,
                      ),
                      growth: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.growth || 0),
                        0,
                      ),
                      estNetRev: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.estNetRev || 0),
                        0,
                      ),
                      estRs: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.estRs || 0),
                        0,
                      ),
                      estCpaIncome: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.estCpaIncome || 0),
                        0,
                      ),
                      totalIncome: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.totalIncome || 0),
                        0,
                      ),
                      spend: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.spend || 0),
                        0,
                      ),
                      cac: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.cac || 0),
                        0,
                      ),
                      clicks: allMonths.reduce(
                        (sum: number, m: any) => sum + (m.clicks || 0),
                        0,
                      ),
                    };

                    // Calculate totals from NonEditableFTD quarters
                    const nonEditableYearTotals =
                      nonEditableQuarterTotals.reduce(
                        (acc, quarter) => ({
                          ftd: acc.ftd + (quarter.ftd || 0),
                          profit: acc.profit + (quarter.profit || 0),
                          growth: acc.growth + (quarter.growth || 0),
                          net_revenue:
                            acc.net_revenue + (quarter.net_revenue || 0),
                          rs: acc.rs + (quarter.rs || 0),
                          cpa: acc.cpa + (quarter.cpa || 0),
                          total_income:
                            acc.total_income + (quarter.total_income || 0),
                          spend: acc.spend + (quarter.spend || 0),
                          cac: acc.cac + (quarter.cac || 0),
                          clicks: acc.clicks + (quarter.clicks || 0),
                        }),
                        {
                          ftd: 0,
                          profit: 0,
                          growth: 0,
                          net_revenue: 0,
                          rs: 0,
                          cpa: 0,
                          total_income: 0,
                          spend: 0,
                          cac: 0,
                          clicks: 0,
                        },
                      );

                    // Combine totals from both components
                    const yearTotals = {
                      ftd: editableYearTotals.ftd + nonEditableYearTotals.ftd,
                      profit:
                        editableYearTotals.profit +
                        nonEditableYearTotals.profit,
                      growth:
                        editableYearTotals.growth +
                        nonEditableYearTotals.growth,
                      estNetRev:
                        editableYearTotals.estNetRev +
                        nonEditableYearTotals.net_revenue,
                      estRs:
                        editableYearTotals.estRs + nonEditableYearTotals.rs,
                      estCpaIncome:
                        editableYearTotals.estCpaIncome +
                        nonEditableYearTotals.cpa,
                      totalIncome:
                        editableYearTotals.totalIncome +
                        nonEditableYearTotals.total_income,
                      spend:
                        editableYearTotals.spend + nonEditableYearTotals.spend,
                      cac: editableYearTotals.cac + nonEditableYearTotals.cac,
                      clicks:
                        editableYearTotals.clicks +
                        nonEditableYearTotals.clicks,
                    };

                    return (
                      <TableRow
                        sx={{
                          backgroundColor: "#FFF3E0",
                          height: 35,
                        }}
                      >
                        <QuarterSummaryCell
                          value={`${selectedYear} Total`}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                          isSticky
                          leftOffset={computedLeftOffsets[0] ?? 0}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          leftOffset={computedLeftOffsets[1]}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          fontWeight={500}
                          isSticky
                          leftOffset={computedLeftOffsets[2]}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          leftOffset={computedLeftOffsets[3]}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          leftOffset={computedLeftOffsets[4]}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          leftOffset={computedLeftOffsets[5]}
                        />
                        <QuarterSummaryCell
                          value={`${formatNumber(yearTotals.profit)}$`}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                          isSticky
                          leftOffset={computedLeftOffsets[6]}
                          hasBorder
                        />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell
                          value={formatNumber(yearTotals.ftd)}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                        />
                        <QuarterSummaryCell
                          value={`${formatNumber(yearTotals.estNetRev)}$`}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                        />
                        <QuarterSummaryCell
                          value={`${formatNumber(yearTotals.estRs)}$`}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                        />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell
                          value={`${formatNumber(yearTotals.estCpaIncome)}$`}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                        />
                        <QuarterSummaryCell
                          value={`${formatNumber(yearTotals.totalIncome)}$`}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                        />
                        <QuarterSummaryCell
                          value={`${formatNumber(yearTotals.clicks)}`}
                          bgColor="#F5F5F5"
                        />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell bgColor="#F5F5F5" />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          fontWeight={500}
                        />
                        <QuarterSummaryCell
                          value={`${formatNumber(yearTotals.spend)}$`}
                          bgColor="#F5F5F5"
                          fontWeight={500}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          rightOffset={computedRightOffsets[23]}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          rightOffset={computedRightOffsets[24]}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          rightOffset={computedRightOffsets[25]}
                        />
                        <QuarterSummaryCell
                          bgColor="#F5F5F5"
                          isSticky
                          rightOffset={computedRightOffsets[26]}
                        />
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
              <Stack
                spacing={1}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              ></Stack>
            </TableContainer>
            <TableWithOffsetScrollbar
              fixedWidth={fixedColumnsWidth}
              scrollRef={tableScrollRef}
            />
          </Paper>
        </>
      )}
      <MetricsByMonthDialogue
        open={openMetrics}
        handleClose={handleClose}
        selectedSiteFromParent={selectedSite}
      />
    </>
  );
};

export default NewForecastForFTD;
